import { Box } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { sectionSpacingY } from "components/section";
import { type Theme } from "contexts/color-theme";
import { useWarehouseCdp } from "contexts/warehouse-cdp-context";
import React from "react";
import Balancer from "react-wrap-balancer";
import { MediaAccordion, type MediaAccordionProps } from "ui/accordion";
import { TextTitle } from "ui/text";
import { integrationsCount } from "../../../constants/integrations";

type WarehouseAccordionProps = {
  theme?: Theme;
};

export function WarehouseAccordion({ theme = "light" }: WarehouseAccordionProps) {
  const { warehouse } = useWarehouseCdp();

  const accordionItems: MediaAccordionProps["items"] = [
    {
      title: `Audience management for ${warehouse.name}`,
      content: `Build self-serve audiences and journeys using every data point in ${warehouse.name} without writing a single line of code.`,
      media: (
        <FillImage
          src={warehouse.accordionImages[0]}
          alt="Abstract illustration of custom audiences getting activated through Hightouch into various ad platforms like TikTok, Meta, and Snapchat."
        />
      ),
    },
    {
      title: `Event Collection and Identity Resolution for ${warehouse.name}`,
      content: `Capture server-side and client-side events directly in ${warehouse.name} and easily stitch your customer data together to build rich, actionable profiles.`,
      media: (
        <FillImage
          src={warehouse.accordionImages[1]}
          alt="An illustration showing an example code snippet for the Hightouch SDK. The code demonstrates using the 'track' function to send an 'Order Completed' event with additional metadata for 'product_category', 'product_id', 'price', and 'used_promo'."
        />
      ),
    },
    {
      title: `Reverse ETL for ${warehouse.name}`,
      content: `Send data from ${warehouse.name} to over ${integrationsCount}+ different destinations in minutes.`,
      media: (
        <FillImage
          src={warehouse.accordionImages[2]}
          alt={`Abstract illustration of ${warehouse.name}'s logo surrounded by tooling logos like Salesforce, Intercom, TikTok, Google Ads, and Meta.`}
        />
      ),
    },
    {
      title: `Data onboarding for ${warehouse.name}`,
      content:
        "Easily upload your audiences to your ad platforms and enrich them in flight to boost match rates, expand LTV, reduce wasted spend, and optimize performance across media channels.",
      media: (
        <FillImage
          src={warehouse.accordionImages[3]}
          alt={`Abstract illustration showing ${warehouse.name}'s logo on the left, with three tooling logos on the right next to an increased percentage indicating boosted match rates. The tooling logos and rates are Meta, up 24%, Snapchat, up 43%, and TikTok, up 52%, respectively.`}
        />
      ),
    },
    {
      title: `AI Decisioning for ${warehouse.name}`,
      content:
        "Leverage reinforcement learning and AI agents to run millions of simultaneous experiments to maximize customer lifetime value and deliver more personalized experiences.",
      media: (
        <FillImage
          src={warehouse.accordionImages[4]}
          alt="Hightouch UI Interface titled 'Personalization insights'. There is a table with three columns titled user attribute, channel, and confidence. The table is populated with rows of data for various user attributes, channels, and confidence scores. For example, New York City responds best to SMS, with a confidence score of 89%."
        />
      ),
    },
  ];

  return (
    <Box my={sectionSpacingY}>
      <TextTitle
        align="center"
        theme={theme}
        size="lg"
        title={<Balancer>A complete CDP for {warehouse.name}</Balancer>}
      />
      <MediaAccordion
        prerenderMedia
        variant="dynamic"
        theme={theme}
        items={accordionItems}
        mt={{ base: "big", md: "biggest" }}
      />
    </Box>
  );
}
